import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { getClientId } from "@/lib/account";

const NAME_KEY = "ideaforge_name";
const inputCls = "rounded-xl bg-[#090A0F] border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500/60 transition-colors";

const timeAgo = (iso) => {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso)) / 60000));
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h fa`;
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
};

const CommentItem = ({ c, index }) => (
  <div className="rounded-2xl bg-[#0d0f17] border border-white/5 p-4" data-testid={`comment-${index + 1}`}>
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-semibold text-amber-200" data-testid={`comment-${index + 1}-name`}>{c.name}</span>
      <span className="text-xs text-slate-500">{timeAgo(c.created_at)}</span>
    </div>
    <p className="text-sm text-slate-300 mt-2 leading-relaxed whitespace-pre-line" data-testid={`comment-${index + 1}-text`}>{c.text}</p>
  </div>
);

const CommentForm = ({ onAdd }) => {
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      localStorage.setItem(NAME_KEY, name);
      await onAdd({ name, text });
      setText("");
      toast.success("Commento pubblicato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile pubblicare il commento.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[12rem_1fr_auto] gap-3" data-testid="comment-form">
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Il tuo nome (facoltativo)" data-testid="comment-name-input" className={inputCls} />
      <input value={text} onChange={(e) => setText(e.target.value)} required minLength={3} maxLength={500} placeholder="Cosa ne pensi di questa idea?" data-testid="comment-text-input" className={inputCls} />
      <button type="submit" disabled={busy || text.trim().length < 3} data-testid="comment-submit-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 transition-colors disabled:opacity-50">
        <Send className="w-4 h-4" /> Pubblica
      </button>
    </form>
  );
};

export const Comments = ({ shareId }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shared/${shareId}/comments`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [shareId]);

  const add = async (body) => {
    const { data } = await axios.post(`${API}/shared/${shareId}/comments`, body, { headers: { "X-Client-Id": getClientId() } });
    setItems((list) => [data, ...(list || [])]);
  };

  return (
    <section className="rounded-3xl bg-[#12141F] border border-white/[0.07] p-6 sm:p-8" data-testid="comments-section">
      <div className="flex items-center gap-3">
        <MessageSquare className="w-5 h-5 text-amber-400" />
        <h2 className="font-display text-lg md:text-xl font-bold">Commenti <span className="text-slate-500 font-normal" data-testid="comments-count">({items ? items.length : "…"})</span></h2>
      </div>
      <p className="text-sm text-slate-400 mt-2">Cosa funziona? Cosa manca? Lascia un commento breve e aiuta chi vuole partire.</p>
      <div className="mt-6"><CommentForm onAdd={add} /></div>
      <div className="mt-6 space-y-3" data-testid="comments-list">
        {items && items.length === 0 && <p className="text-sm text-slate-500" data-testid="comments-empty">Nessun commento ancora. Rompi il ghiaccio!</p>}
        {items && items.map((c, i) => <CommentItem key={c.id} c={c} index={i} />)}
      </div>
    </section>
  );
};
